import { ReactNode } from 'react';
import { cn } from '@Utils';

interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  ariaLabel?: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange?: (value: T) => void;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
  optionClassName?: string;
  activeOptionClassName?: string;
  inactiveOptionClassName?: string;
}

const DEFAULT_OPTION_CLASSNAME = 'flex-1 rounded-md py-2 text-center text-sm font-semibold transition-colors';
const DEFAULT_ACTIVE_CLASSNAME = 'bg-white text-gray-900 shadow-sm';
const DEFAULT_INACTIVE_CLASSNAME = 'text-gray-500 hover:text-gray-700';

const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  className,
  optionClassName = DEFAULT_OPTION_CLASSNAME,
  activeOptionClassName = DEFAULT_ACTIVE_CLASSNAME,
  inactiveOptionClassName = DEFAULT_INACTIVE_CLASSNAME,
}: SegmentedControlProps<T>) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className={cn('flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1', className)}>
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-label={option.ariaLabel}
          title={option.ariaLabel}
          disabled={disabled}
          onClick={() => onChange?.(option.value)}
          className={cn(
            'disabled:pointer-events-none disabled:opacity-50',
            optionClassName,
            isActive ? activeOptionClassName : inactiveOptionClassName,
          )}>
          {option.label}
        </button>
      );
    })}
  </div>
);

export default SegmentedControl;
export type { SegmentedControlOption };
